import { createApi } from '@reduxjs/toolkit/query/react';
import { ApiBaseName, ApiReducerPath } from '@Enum/index';
import { Contact, ContactListResponse } from '@Components/ContactList/types';
import { customBaseQuery } from '@Utils/api/customBaseQuery';

// Define the search criteria interface based on the API structure
export interface ContactSearchCriteria {
  name: string;
  value: string | number;
  operator: string;
}

export interface ContactSearchRequest {
  searchValue: string;
  searchCriteriaList: Array<{
    searchCriterias: ContactSearchCriteria[];
    dataOperator: string;
  }>;
  dataOperator: string;
}

export interface ContactSearchParams {
  page: number;
  size: number;
  'sort-order': string;
  'sort-by'?: string;
  body: ContactSearchRequest;
}

// Field mapping from frontend to API
export const FIELD_MAPPING: { [key: string]: string } = {
  employeeNumber: 'empNumber',
  employeeName: 'empName',
  occupation: 'occupation',
  email: 'email1',
  mobile: 'mobile1',
  extension: 'extension',
  workLocation: 'workLocation',
  department: 'deptProjId',
};

// Sort field mapping - maps frontend field names to API field names for sorting
const SORT_FIELD_MAPPING: { [key: string]: string } = {
  empNumber: 'empNumber',
  empName: 'empName',
  occupation: 'occupation',
  empType: 'empType',
  email1: 'email1',
  mobile1: 'mobile1',
  mobile2: 'mobile2',
  whatsappNumber: 'whatsappNumber',
  extension: 'extension',
  tanumber: 'tanumber',
};

export const contactApi = createApi({
  reducerPath: ApiReducerPath.API_CONTACT,
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    searchContacts: builder.mutation<ContactListResponse, ContactSearchParams>({
      query: ({ page, size, 'sort-order': sortOrder, 'sort-by': sortBy, body }) => ({
        url: `${ApiBaseName.UMS}/contact/search`,
        method: 'POST',
        params: { 
          page, 
          size, 
          'sort-order': sortOrder,
          ...(sortBy && { 'sort-by': sortBy })
        },
        body,
      }),
      transformResponse: (response: any) => {
        // Transform the API response to match our ContactListResponse interface
        return {
          content: response.content.map((item: any) => ({
            contactId: item.contactId,
            empNumber: item.empNumber,
            empName: item.empName,
            occupation: item.occupation,
            email: item.email || '',
            mobile1: item.mobile1 || '',
            mobile1CUG: item.mobile1CUG || '',
            email1: item.email1,
            email2: item.email2,
            email3: item.email3,
            mobile2: item.mobile2,
            extension: item.extension,
            isCUG: item.isCUG,
            tanumber: item.tanumber,
            isStaff: item.isStaff,
            firstName: item.firstName,
            middleName: item.middleName,
            lastName: item.lastName,
            compId: item.compId,
            empType: item.empType || '',
            whatsappNumber: item.whatsappNumber || '',
          })),
          totalElements: response.totalElements,
          totalPages: response.totalPages,
          size: response.size,
          page: response.page,
          numberOfElements: response.numberOfElements,
        };
      },
    }),
  }),
});

export const { useSearchContactsMutation } = contactApi; 